import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import SaveIcon from '@mui/icons-material/Save';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import { pieceDetachmentEndpoints, requestPieceDetachment } from '../../../utils/pieceDetachment';
import FilesOpener from '../../../components/CGForm/FilesOpener';
import usePreDialogValidation from '../../../hooks/usePreDialogValidation';
import { useDAData } from '../../../hooks/useApiQueries';
import theme from '../../../theme';
import {
  cleanSiret,
  documentTypesDb,
  formatRegPlate,
  formatSiret,
  formatUTCDateTime,
  getAdresseTypeVoie,
  getFormatFromMimetype,
  removeSpaces,
  resolveVoieCode,
  validateSiret,
} from '../../../utils';
import { isSubmissionAlreadyAtMinistry } from '../../../utils/daSubmissionErrors';
import { getCommonValidationRules } from '../../../utils/formValidationRules';
import { resolveTypeAndNomVoie } from '../../../utils/normalizeTypeVoie';
import FirebaseUploadComponent from '../../CreateCotation/FirebaseUploadComponent';
import CfnPiecesWarning from './CfnPiecesWarning';
import ManualDAReceipt from './ManualDAReceipt';
import { ErrorMessageSnackbar } from '../../ErrorMessageSnackbar';
import FileCarouselDialog from '../../FileCarouselDialog';

interface DAData {
  id: number;
  reg_plate: string;
  vin: string;
  num_formule: string;
  purchase_date_time: string;
  status: string;
  org_id?: number;
  person_id?: number;
  created_at: string;
  updated_at: string;
  visible: boolean;
  buyer?: {
    id: number;
    registration_id: number;
    client_type: 'person' | 'pro';
    first_name?: string;
    last_name?: string;
    usual_name?: string;
    gender?: string;
    date_of_birth?: string;
    place_of_birth?: string;
    country_of_birth?: string;
    department_of_birth?: string;
    address_number?: string;
    address_extension?: string;
    address_type?: string;
    address_name?: string;
    address_place?: string;
    address_stairs?: string;
    address_comp?: string;
    address_postal_box?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    phone?: string;
    siret?: string;
    company_name?: string;
    files?: string;
    residency_files?: string;
    document_type?: string;
    document_id?: string;
    document_issue_date?: string;
  };
  seller?: {
    id: number;
    registration_id: number;
    client_type: 'person' | 'pro';
    first_name?: string;
    last_name?: string;
    usual_name?: string;
    gender?: string;
    date_of_birth?: string;
    place_of_birth?: string;
    country_of_birth?: string;
    department_of_birth?: string;
    address_number?: string;
    address_extension?: string;
    address_type?: string;
    address_name?: string;
    address_place?: string;
    address_stairs?: string;
    address_comp?: string;
    address_postal_box?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    siret?: string;
    company_name?: string;
    files?:
      | {
          certificate?: string[];
          cerfa_achat?: string[];
          cerfa_cession?: string[];
          mandat?: string[];
          recepisse?: string[];
        }
      | string;
  };
  misiv?: {
    id: number;
    registration_id: number;
    response_id?: number;
    receipt_file_name?: string;
    receipt_file_firebase_url?: string;
    has_timeout: number;
    lpn_id?: string;
    step: number;
    created_at: string;
    updated_at: string;
  };
}

interface SubmitFormDAProps {
  registrationId: number;
  userRole?: string;
  onRefresh?: () => void;
}

const SubmitFormDA: React.FC<SubmitFormDAProps> = ({ registrationId, userRole, onRefresh }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingData, setIsSavingData] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [purchaseDateTime, setPurchaseDateTime] = useState('');
  const [responseErrorCode, setResponseErrorCode] = useState(null);
  const [buttonText, setButtonText] = useState<string>('');
  const [buttonStep, setButtonStep] = useState<string>('initial');
  const [responseNomFic, setResponseNomFic] = useState<string>('');
  const [responseId, setResponseId] = useState<number>();
  const [errorText, setErrorText] = useState<string>('');
  const [isTimeoutError, setIsTimeoutError] = useState(false);

  const validation = usePreDialogValidation();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // États pour le progress et les étapes
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');

  // États pour la gestion des documents
  const [downloading, setDownloading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);

  // Carousel state
  const [carouselOpen, setCarouselOpen] = useState<boolean>(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);

  const env = window.location.hostname === 'app.skipcar.fr' ? 'production' : 'development';

  const { data: daData, refetch: refetchDAData } = useDAData(registrationId.toString()) as {
    data: DAData | undefined;
    refetch: () => void;
  };

  const queryClient = useQueryClient();

  const formMethods = useForm({
    mode: 'all',
  });
  const { handleSubmit, control, formState, trigger, watch } = formMethods;
  const { errors, isValid } = formState;

  const sellerClientType = watch('sellerClientType');

  const validationRules = getCommonValidationRules(formMethods);

  const allCarouselFiles = useMemo(() => {
    if (!daData) return [];
    const urls: string[] = [];
    const addUrls = (source: string[] | undefined | null) => {
      if (!source) return;
      source.forEach((u) => {
        if (u && !urls.includes(u)) urls.push(u);
      });
    };
    const addUrlString = (source: string | undefined | null) => {
      if (!source) return;
      source
        .split(',')
        .map((u) => u.trim())
        .forEach((u) => {
          if (u && !urls.includes(u)) urls.push(u);
        });
    };

    // Seller structured files
    if (daData.seller?.files && typeof daData.seller.files === 'object') {
      const sf = daData.seller.files as any;
      addUrls(sf.certificate);
      addUrls(sf.cerfa_achat);
      addUrls(sf.cerfa_cession);
      addUrls(sf.mandat);
      addUrls(sf.recepisse);
    }

    // Buyer files
    if (daData.buyer?.files && typeof daData.buyer.files === 'string') {
      addUrlString(daData.buyer.files);
    }
    if (daData.buyer?.residency_files && typeof daData.buyer.residency_files === 'string') {
      addUrlString(daData.buyer.residency_files);
    }

    // Comment files
    addUrlString((daData as any).commentFilesUrls);
    addUrlString((daData as any).internalCommentFilesUrls);

    return urls;
  }, [daData]);

  const handleCarouselOpen = (url: string) => {
    const index = allCarouselFiles.indexOf(url);
    setCarouselIndex(index >= 0 ? index : 0);
    setCarouselOpen(true);
  };

  useEffect(() => {
    const determineButtonState = () => {
      if (!daData) return 'initial';

      const { misiv } = daData;
      if (!misiv) {
        // Gestion des boutons spécifiques pour les agences quand il n'y a pas encore de données misiv
        if (userRole === 'agency') {
          if (daData.status === 'DA_REQUEST') {
            return 'waiting-payment';
          } else if (daData.status === 'DA_PROCESSING') {
            return 'waiting-procedures';
          }
        }
        return 'initial';
      }

      const { receipt_file_name, response_id, has_timeout, lpn_id, step, updated_at } = misiv;

      // PRIORITÉ ABSOLUE: Cas où on a le receipt_file_name mais pas le receipt_file_firebase_url
      // Cette condition doit être vérifiée en premier, même pour les agences
      if (
        receipt_file_name &&
        response_id &&
        has_timeout === 0 &&
        (!misiv.receipt_file_firebase_url ||
          misiv.receipt_file_firebase_url === null ||
          misiv.receipt_file_firebase_url === '')
      ) {
        setResponseId(response_id);
        setIsTimeoutError(false);
        setButtonText(`⚠️ RÉGÉNÉRER DA`);
        return 'regen';
      }

      // Gestion des boutons spécifiques pour les agences
      if (userRole === 'agency') {
        if (daData.status === 'DA_REQUEST') {
          return 'waiting-payment';
        } else if (daData.status === 'DA_PROCESSING') {
          return 'waiting-procedures';
        }
      }

      // Si le statut est DA_COMPLETED, afficher le bouton vert pour tous les utilisateurs
      if (daData.status === 'DA_COMPLETED' && receipt_file_name && response_id && has_timeout === 0) {
        setResponseNomFic(receipt_file_name);
        if (userRole === 'agency') {
          setButtonText(`✅ Télécharger récépissé DA`);
        } else {
          setButtonText(`✅ DA n°${response_id}`);
        }
        setIsTimeoutError(false);
        return 'receipt';
      }

      // Cas où on a receipt_file_firebase_url mais pas receipt_file_name - bouton vert quand même
      if (misiv.receipt_file_firebase_url && response_id && has_timeout === 0 && !receipt_file_name) {
        setResponseNomFic(''); // Pas de nom de fichier mais on a l'URL Firebase
        if (userRole === 'agency') {
          setButtonText(`✅ Télécharger récépissé DA`);
        } else {
          setButtonText(`✅ DA n°${response_id}`);
        }
        setIsTimeoutError(false);
        return 'receipt';
      }

      // DA enregistrée par le « + » (faite hors plateforme) : récépissé sans
      // numéro de demande MiSIV. Sans ce cas le dossier retombait sur
      // « Soumettre la DA » alors qu'il est traité.
      if (misiv.receipt_file_firebase_url && !response_id && has_timeout !== 1) {
        setResponseNomFic(receipt_file_name ?? '');
        setButtonText(userRole === 'agency' ? `✅ Télécharger récépissé DA` : `✅ DA externe`);
        setIsTimeoutError(false);
        return 'receipt';
      }

      if (receipt_file_name && response_id && has_timeout === 0) {
        setResponseNomFic(receipt_file_name);
        if (userRole === 'agency') {
          setButtonText(`✅ Télécharger récépissé DA`);
        } else {
          setButtonText(`✅ DA n°${response_id}`);
        }
        setIsTimeoutError(false);

        return 'receipt';
      } else if (!receipt_file_name && has_timeout === 1) {
        setResponseId(response_id);
        setIsTimeoutError(true);
        setButtonText(`Renvoyer au ministère n°${response_id}`);
        return 'timeout-error';
      } else if (!receipt_file_name && has_timeout === 0) {
        setPurchaseDateTime(daData.purchase_date_time);
        setIsTimeoutError(false);
        setButtonText(`⚠️ DA n°${response_id}`);
        return 'save';
      }

      return 'initial';
    };

    const newState = determineButtonState();
    setButtonStep(newState);
  }, [daData, userRole]);

  const handleConfirmClick = () => {
    setIsConfirmModalOpen(true);
  };

  const handleFinalConfirm = async () => {
    setIsConfirmModalOpen(false); // Fermer la modale de confirmation immédiatement
    setIsLoading(true);
    setProgress(0);
    setCurrentStep('Initialisation');

    try {
      const formData = formMethods.getValues();

      // Étape 1: Vérifier et mettre à jour la registration si nécessaire
      setProgress(10);
      setCurrentStep('Vérification des modifications de la registration');
      const registrationChanged = await updateRegistrationIfChanged(formData);

      // Étape 2: Détecter et créer de nouveaux enregistrements si nécessaire
      setProgress(20);
      setCurrentStep('Vérification des modifications des participants');

      const buyerChanged = await createNewBuyerIfChanged(formData);
      const sellerChanged = await createNewSellerIfChanged(formData);

      if (buyerChanged || sellerChanged || registrationChanged) {
        // Rafraîchir les données pour avoir les dernières versions
        setCurrentStep('Données mises à jour');
      }

      // Étape 3: Sauvegarde des données (si pas de changements majeurs)
      setProgress(35);
      setCurrentStep('Sauvegarde des données');
      if (!buyerChanged) {
        await handleSave(formData);
      }

      // Étape 4: Sauvegarde du certificat
      setProgress(60);
      setCurrentStep('Sauvegarde du certificat');
      await handleSavePurchaseCertificate(formData);

      // Étape 5: Soumission
      setProgress(85);
      setCurrentStep('Soumission au ministère');
      await handleSubmitPurchase(formData);

      // Étape 6: Finalisation
      setProgress(100);
      setCurrentStep('Finalisation');

      // Fermer le dialog principal seulement en cas de succès
      handleDialogClose();
    } catch (error) {
      console.error('Erreur lors du processus:', error);
      // En cas d'erreur, le dialog principal reste ouvert pour que l'utilisateur puisse corriger
    } finally {
      setIsLoading(false);
    }
  };

  const checkBuyerInformations = async () => {
    if (!daData?.buyer) {
      validation.setTooltipError("Les informations de l'acheteur sont absentes");
      return true;
    }

    const buyer = daData.buyer;
    if (!buyer.postal_code) {
      validation.setTooltipError("Le code postal de l'acheteur est absent");
      return true;
    }

    return false;
  };

  const openSnackbar = validation.openSnackbar;

  const handleDownloadDocuments = async () => {
    setDownloading(true);
    const zip = new JSZip();
    const folder = zip.folder(`Skipcar-DA-${registrationId}`);

    const allFiles: string[] = [];

    // Fonction utilitaire pour extraire les URLs d'un objet files
    const extractFilesFromObject = (filesObject: any, prefix: string) => {
      if (!filesObject || typeof filesObject !== 'object') return [];

      const extractedFiles: string[] = [];
      Object.keys(filesObject).forEach((category) => {
        const categoryFiles = filesObject[category];
        if (Array.isArray(categoryFiles)) {
          extractedFiles.push(...categoryFiles);
        }
      });
      return extractedFiles;
    };

    // Fonction utilitaire pour traiter les fichiers en format string (legacy)
    const extractFilesFromString = (filesString: string, prefix: string) => {
      if (typeof filesString === 'string') {
        const files = filesString
          .split(',')
          .map((url: string) => url.trim())
          .filter(Boolean);
        return files;
      }
      return [];
    };

    // Collecter tous les fichiers disponibles
    if (daData?.buyer?.files) {
      if (typeof daData.buyer.files === 'object') {
        const buyerFiles = extractFilesFromObject(daData.buyer.files, 'buyer');
        allFiles.push(...buyerFiles);
      } else if (typeof daData.buyer.files === 'string') {
        const buyerFiles = extractFilesFromString(daData.buyer.files, 'buyer');
        allFiles.push(...buyerFiles);
      } else {
        console.warn('buyer.files is not a string or object:', daData.buyer.files);
      }
    }

    if (daData?.buyer?.residency_files) {
      if (typeof daData.buyer.residency_files === 'object') {
        const residencyFiles = extractFilesFromObject(daData.buyer.residency_files, 'buyer.residency');
        allFiles.push(...residencyFiles);
      } else if (typeof daData.buyer.residency_files === 'string') {
        const residencyFiles = extractFilesFromString(daData.buyer.residency_files, 'buyer.residency');
        allFiles.push(...residencyFiles);
      } else {
        console.warn('buyer.residency_files is not a string or object:', daData.buyer.residency_files);
      }
    }

    if (daData?.seller?.files) {
      if (typeof daData.seller.files === 'object') {
        const sellerFiles = extractFilesFromObject(daData.seller.files, 'seller');
        allFiles.push(...sellerFiles);
      } else if (typeof daData.seller.files === 'string') {
        const sellerFiles = extractFilesFromString(daData.seller.files, 'seller');
        allFiles.push(...sellerFiles);
      } else {
        console.warn('seller.files is not a string or object:', daData.seller.files);
      }
    }

    // Ajouter les fichiers uploadés via le composant
    allFiles.push(...uploadedUrls);

    // Ajouter les fichiers de commentaires externes
    if (daData?.commentFilesUrls) {
      const commentFiles = daData.commentFilesUrls
        .split(',')
        .map((url: string) => url.trim())
        .filter(Boolean);
      allFiles.push(...commentFiles);
    }

    const promises = allFiles.map(async (url: string, index: number) => {
      try {
        const response = await axios.get(url, { responseType: 'arraybuffer' });
        const blob = new Blob([response.data], { type: String(response.headers['content-type'] ?? '') });
        const fileName = `Skipcar-DA-${registrationId}-${index}.${getFormatFromMimetype(String(response.headers['content-type'] ?? ''))}`;
        folder?.file(fileName, blob);
      } catch (error) {
        console.error('Error handling document:', error);
      }
    });

    await Promise.all(promises);

    try {
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `Skipcar-DA-${registrationId}.zip`);
    } catch (error) {
      console.error('Error generating ZIP:', error);
    } finally {
      setDownloading(false);
    }
  };

  // Les pièces listées ici partent au coffre-fort numérique : un dépôt est
  // rattaché à l'acquéreur courant (sans ça il restait orphelin), un retrait
  // enlève la référence en base AVANT la suppression Storage.
  const handleUploadComplete = async (url: string) => {
    setUploadedUrls((prevUrls) => [...prevUrls, url]);
    try {
      await makeAuthenticatedApiCall('post', pieceDetachmentEndpoints.da(registrationId), { url });
    } catch (error: any) {
      console.error('SubmitFormDA / rattachement de la pièce au dossier :', JSON.stringify(error));
      openSnackbar(
        error?.response?.data?.error || 'Pièce déposée mais non rattachée au dossier : elle ne partira pas au coffre.',
        'error',
      );
    }
  };

  const handleBeforeDelete = (url: string) => requestPieceDetachment(pieceDetachmentEndpoints.da(registrationId), url);

  const handleDeleteComplete = (url: string) => {
    setUploadedUrls((prevUrls) => prevUrls.filter((existingUrl) => existingUrl !== url));
    refetchDAData();
  };

  const existingBuyerFiles = [daData?.buyer?.files, daData?.buyer?.residency_files]
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
    .flatMap((value) => value.split(',').map((u) => u.trim()))
    .filter(Boolean);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleSave = async (formData: any) => {
    try {
      const submissionData = {
        ...formData,
        documentType: documentTypesDb[formData.documentType] || formData.documentType,
      };

      await makeAuthenticatedApiCall('patch', `/api/da/buyers/${registrationId}`, submissionData);
      openSnackbar('Les modifications ont bien été prises en compte', 'success');
    } catch (error: any) {
      console.error(error);
      openSnackbar(error.message, 'error');
      validation.setDisabledButton(false);
      throw error;
    }
  };

  // Fonction pour enregistrer toutes les données sans soumettre à MISIV
  const handleSaveData = async () => {
    setIsSavingData(true);
    try {
      // Récupérer toutes les données du formulaire
      const formData = formMethods.getValues();

      // 1. Sauvegarder les données du buyer
      await handleSave(formData);

      // 2. Sauvegarder les données du seller (ancien propriétaire)
      const sellerData = {
        clientType: formData.sellerClientType || daData?.seller?.client_type,
        firstName: formData.sellerFirstName,
        lastName: formData.sellerLastName,
        companyName: formData.sellerCompanyName,
        siret: formData.sellerSiret,
      };

      await makeAuthenticatedApiCall('patch', `/api/da/sellers/${registrationId}`, sellerData);

      // 3. Sauvegarder les données du DA registration (VIN, num_formule, etc.)
      const registrationData = {
        regPlate: formatRegPlate(formData.regPlate || ''),
        vin: removeSpaces(formData.vin || ''),
        numFormule: removeSpaces(formData.numFormule || ''),
        purchaseDateTime: purchaseDateTime,
      };

      await makeAuthenticatedApiCall('patch', `/api/da/registrations/${registrationId}`, registrationData);

      // Invalider et refetch les queries pour actualiser les données
      // `useDAData` enregistre la clé avec un registrationId en string : invalider
      // avec le number ne matchait rien et les deux appels étaient des no-op.
      await queryClient.invalidateQueries({ queryKey: ['daData', registrationId.toString()] });
      await queryClient.refetchQueries({ queryKey: ['daData', registrationId.toString()] });

      openSnackbar('Données enregistrées avec succès', 'success');
    } catch (error: any) {
      console.error("Erreur lors de l'enregistrement:", error);
      openSnackbar(error?.message || "Erreur lors de l'enregistrement des données", 'error');
    } finally {
      setIsSavingData(false);
    }
  };

  const handleSavePurchaseCertificate = async (formData?: any) => {
    try {
      const mergedData = await getMergedData(formData);
      if (!mergedData) return;

      // Utiliser la date du formulaire si disponible, sinon celle de l'état
      const purchaseDateToUse = formData?.purchaseDateTime || purchaseDateTime;

      const dealData = {
        regPlate: mergedData.reg_plate,
        vin: mergedData.vin,
        numFormule: mergedData.num_formule,
        purchaseDateTime: purchaseDateToUse,
        registrationId: registrationId,
        // Informations du vendeur
        sellerClientType: mergedData.seller?.client_type,
        sellerFirstName: mergedData.seller?.first_name,
        sellerLastName: mergedData.seller?.last_name,
        sellerCompanyName: mergedData.seller?.company_name,
        sellerSiret: mergedData.seller?.siret,
        // Informations de l'acheteur
        buyerClientType: mergedData.buyer?.client_type,
        buyerFirstName: mergedData.buyer?.first_name,
        buyerLastName: mergedData.buyer?.last_name,
        buyerGender: mergedData.buyer?.gender,
        buyerUsualName: mergedData.buyer?.usual_name,
        buyerCompanyName: mergedData.buyer?.company_name,
        buyerSiret: mergedData.buyer?.siret,
        buyerAddressNumber: mergedData.buyer?.address_number,
        buyerAddressExtension: mergedData.buyer?.address_extension,
        buyerAddressType: mergedData.buyer?.address_type,
        buyerAddressName: mergedData.buyer?.address_name,
        buyerAddressZipCode: mergedData.buyer?.postal_code,
        buyerAddressCity: mergedData.buyer?.city,
        buyerDateOfBirth: mergedData.buyer?.date_of_birth,
        buyerPlaceOfBirth: mergedData.buyer?.place_of_birth,
        buyerDocumentType: mergedData.buyer?.document_type,
        buyerDocumentId: mergedData.buyer?.document_id,
        buyerDocumentIssueDate: mergedData.buyer?.document_issue_date,
      };

      const responseSave = await makeAuthenticatedApiCall('post', `/api/misiv/form-da/save/purchase`, dealData);
      const responseSubmitId = (responseSave?.data as any)?.responseId;
      setButtonStep('save');
      setButtonText(`⚠️ DA n°${responseSubmitId}`);
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde de la demande:', error);
      // On affiche le message renvoyé par le backend pour toute erreur HTTP (400 validation,
      // 500 SOAP fault MISIV, etc.). Ça remplace le générique "Erreur lors de la sauvegarde".
      const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
      openSnackbar(backendMessage || 'Erreur lors de la sauvegarde de la demande', 'error');
      validation.setDisabledButton(false);
      setButtonStep('initial');
      throw error;
    }
  };

  const handleSubmitPurchase = async (formData?: any) => {
    try {
      const mergedData = await getMergedData(formData);
      if (!mergedData) return;

      // Utiliser la date du formulaire si disponible, sinon celle de l'état
      const purchaseDateToUse = formData?.purchaseDateTime || purchaseDateTime;

      console.log('🚀 handleSubmitPurchase - Données utilisées pour MISIV:', {
        regPlate: mergedData.reg_plate,
        vin: mergedData.vin,
        numFormule: mergedData.num_formule,
        purchaseDateTime: purchaseDateToUse,
        originalPurchaseDateTime: purchaseDateTime,
        formDataPurchaseDateTime: formData?.purchaseDateTime,
      });

      const dealData = {
        regPlate: mergedData.reg_plate,
        vin: mergedData.vin,
        numFormule: mergedData.num_formule,
        purchaseDateTime: purchaseDateToUse,
        registrationId: registrationId,
        // Informations du vendeur
        sellerClientType: mergedData.seller?.client_type,
        sellerFirstName: mergedData.seller?.first_name,
        sellerLastName: mergedData.seller?.last_name,
        sellerCompanyName: mergedData.seller?.company_name,
        sellerSiret: mergedData.seller?.siret,
        // Informations de l'acheteur
        buyerClientType: mergedData.buyer?.client_type,
        buyerFirstName: mergedData.buyer?.first_name,
        buyerLastName: mergedData.buyer?.last_name,
        buyerGender: mergedData.buyer?.gender,
        buyerUsualName: mergedData.buyer?.usual_name,
        buyerCompanyName: mergedData.buyer?.company_name,
        buyerSiret: mergedData.buyer?.siret,
        buyerAddressNumber: mergedData.buyer?.address_number,
        buyerAddressExtension: mergedData.buyer?.address_extension,
        buyerAddressType: mergedData.buyer?.address_type,
        buyerAddressName: mergedData.buyer?.address_name,
        buyerAddressZipCode: mergedData.buyer?.postal_code,
        buyerAddressCity: mergedData.buyer?.city,
        buyerDateOfBirth: mergedData.buyer?.date_of_birth,
        buyerPlaceOfBirth: mergedData.buyer?.place_of_birth,
        buyerDocumentType: mergedData.buyer?.document_type,
        buyerDocumentId: mergedData.buyer?.document_id,
        buyerDocumentIssueDate: mergedData.buyer?.document_issue_date,
      };

      console.log('📤 handleSubmitPurchase - dealData complet envoyé:', dealData);

      const responseSubmit = await makeAuthenticatedApiCall('post', `/api/misiv/form-da/submit/purchase`, dealData);

      const responseId = (responseSubmit.data as any)?.responseId;
      const responseNomFic = (responseSubmit.data as any)?.responseNomFic;
      setResponseNomFic(responseNomFic);

      openSnackbar(`Votre demande a bien été envoyée : ${responseId}`, 'success');
      setButtonStep('receipt');
      if (userRole === 'agency') {
        setButtonText(`✅ Télécharger récépissé DA`);
      } else {
        setButtonText(`✅ DA n°${responseId}`);
      }

      const firebaseUrl = (responseSubmit.data as any)?.firebaseReceipt?.firebaseReceiptUrl;
      if (firebaseUrl) {
        window.open(firebaseUrl);
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error: any) {
      // DA acceptée par le SIV mais récépissé pas encore récupérable (500
      // receiptPending), ou soumission refusée parce que la DA y est déjà (409).
      // Dans les deux cas le véhicule EST déclaré : la seule action utile est
      // « Régénérer DA », qui rouvre la démarche existante. Sans cette bascule
      // le bouton restait sur « Soumettre » et l'agence renvoyait la démarche —
      // DP-853-FV a fini avec cinq déclarations d'achat au ministère (20/08/2026).
      if (isSubmissionAlreadyAtMinistry(error)) {
        const ministryResponseId = error.response.data?.responseId;
        const ministryMessage = error.response.data?.message;
        setResponseId(ministryResponseId);
        setIsTimeoutError(false);
        setButtonStep('regen');
        setButtonText(`⚠️ RÉGÉNÉRER DA`);
        openSnackbar(
          ministryMessage ||
            `DA enregistrée au ministère n°${ministryResponseId} : récépissé indisponible, utilisez « Régénérer DA »`,
          'error',
        );
        setErrorText(ministryMessage || '');
        if (onRefresh) {
          onRefresh();
        }
        return;
      }

      if (error.response && error.response.status === 500) {
        const responseId = error.response.data?.responseId;
        const responseErrorCode = error.response.data?.responseErrorCode;
        const responseErrorMessage = error.response.data?.responseErrorMessage;

        if (responseErrorCode === 3 || responseErrorCode === 100) {
          setResponseId(responseId);
          setIsTimeoutError(true);
          setButtonText(`Renvoyer au ministère n°${responseId}`);
          openSnackbar(`Timeout Ministère : Ré-essayer dans quelques instants`, 'error');
          return;
        } else {
          setResponseErrorCode(responseErrorCode);
        }

        if (responseErrorCode) {
          openSnackbar(`Erreur ${responseErrorCode} : ${responseErrorMessage}`, 'error');
          setErrorText(responseErrorMessage);
        } else {
          // 500 sans responseErrorCode = SOAP fault MISIV ou erreur interne : on lit .message en priorité
          // (extractMisivSoapError côté backend remplit ce champ), .error en fallback historique.
          const fallbackMessage = error.response.data?.message || error.response.data?.error;
          openSnackbar(fallbackMessage || "Erreur lors de l'envoi du certificat à TMS", 'error');
          setErrorText(fallbackMessage || '');
        }
      } else if (error.response && error.response.status === 400) {
        // Erreurs de validation côté backend (SIRET invalide, adresse incomplète, etc.)
        const validationMessage = error.response.data?.message || error.response.data?.error;
        openSnackbar(validationMessage || 'Erreur de validation', 'error');
        setErrorText(validationMessage || '');
      } else if (axios.isAxiosError(error)) {
        console.error('Erreur réseau ou serveur :', error.message);
        openSnackbar('Erreur réseau ou serveur : ' + error.message, 'error');
      } else {
        console.error("Erreur inattendue lors de l'envoi du certificat à TMS :", error);
        openSnackbar(`Erreur lors de l'envoi du certificat à TMS`, 'error');
      }
      throw error;
    } finally {
      validation.setDisabledButton(false);
    }
  };

  const handleResendToMinistry = async (responseSubmitId: number) => {
    console.log(`🔵 [FRONTEND] handleResendToMinistry - Début avec responseSubmitId: ${responseSubmitId}`);
    setIsLoading(true);

    try {
      console.log(`🔵 [FRONTEND] Appel API /api/misiv/open/purchase avec:`, {
        responseSubmitId,
        pipelineLetter: 'da',
      });

      const responseOpen = await makeAuthenticatedApiCall('post', `/api/misiv/open/purchase`, {
        responseSubmitId,
        pipelineLetter: 'da',
      });

      console.log(`🔵 [FRONTEND] Réponse API openPurchase:`, responseOpen.data);

      handleGetReceipt((responseOpen.data as any)?.jsonObj);
      setButtonStep('receipt');
      setButtonText(`✅ DA n°${responseSubmitId}`);
      validation.setTooltipError('');
      setResponseNomFic((responseOpen.data as any)?.jsonObj);

      console.log(`🔵 [FRONTEND] handleResendToMinistry - Succès, button step: receipt`);
    } catch (error: any) {
      console.error("🔴 [FRONTEND] Erreur lors de l'ouverture du certificat à TMS :", error);
      openSnackbar(error.response.data.error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetReceipt = async (responseNomFic: string) => {
    console.log(
      `🔵 [FRONTEND] handleGetReceipt - Début avec responseNomFic: "${responseNomFic}", registrationId: ${registrationId}`,
    );
    setIsLoading(true);

    try {
      // Si on n'a pas de responseNomFic mais qu'on a l'URL Firebase directement
      if (!daData?.misiv?.receipt_file_name && daData?.misiv?.receipt_file_firebase_url) {
        console.log(`🔵 [FRONTEND] Ouverture directe Firebase URL:`, daData.misiv.receipt_file_firebase_url);

        // 🎯 DÉCLENCHER LA COMPLETION AUTOMATIQUE même quand on a déjà l'URL Firebase
        try {
          console.log(`🔵 [FRONTEND] Appel API completion DA pour registrationId: ${registrationId}`);

          // Appel API pour déclencher la completion automatique
          await makeAuthenticatedApiCall('post', `/api/misiv/receipt/purchase`, {
            responseNomFic: '', // Vide car on a déjà l'URL
            pipelineLetter: 'da',
            dealId: registrationId,
            typeDemarche: 'DA',
            directFirebaseUrl: true, // Flag pour indiquer qu'on a déjà l'URL
          });

          console.log(`🔵 [FRONTEND] Completion DA déclenchée avec succès`);
        } catch (error) {
          console.error('🔴 [FRONTEND] Erreur lors du déclenchement de completion DA:', error);
          // Ne pas bloquer l'ouverture du fichier
        }

        window.open(daData.misiv.receipt_file_firebase_url);
        setButtonStep('receipt');
        setIsTimeoutError(false);
        return;
      }

      // Cas normal avec responseNomFic
      console.log(`🔵 [FRONTEND] Appel API /api/misiv/receipt/purchase avec:`, {
        responseNomFic,
        pipelineLetter: 'da',
        dealId: registrationId,
        typeDemarche: 'DA',
      });

      const responseReceipt = await makeAuthenticatedApiCall('post', `/api/misiv/receipt/purchase`, {
        responseNomFic,
        pipelineLetter: 'da',
        dealId: registrationId,
        typeDemarche: 'DA',
      });

      console.log(`🔵 [FRONTEND] Réponse API receiptPurchase:`, responseReceipt.data);

      const firebaseUrl = (responseReceipt.data as any)?.firebaseUrl;
      if (firebaseUrl) {
        console.log(`🔵 [FRONTEND] Ouverture Firebase URL:`, firebaseUrl);
        window.open(firebaseUrl);
      }
      setButtonStep('receipt');
      setIsTimeoutError(false);

      console.log(`🔵 [FRONTEND] handleGetReceipt - Succès`);
    } catch (error: any) {
      console.error('🔴 [FRONTEND] handleGetReceipt - Erreur lors de la génération du reçu :', error);
      openSnackbar(error.response.data.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = async () => {
    const hasError = await checkBuyerInformations();

    if (!hasError) {
      validation.resetValidation();
      if (buttonText.startsWith('⚠️ DA n°') && (responseErrorCode === 3 || responseErrorCode === 100)) {
        // Récupérer les données du formulaire si elles existent
        const formData = formMethods.getValues();
        handleSubmitPurchase(formData);
      } else {
        setIsDialogOpen(true);
      }
    }
  };

  useEffect(() => {
    if (isDialogOpen) {
      trigger();
    }
  }, [isDialogOpen, trigger]);

  // Un seul reset pour tout le formulaire, rejoué à chaque changement de dossier.
  //
  // Il couvre le véhicule (immatriculation, VIN, n° de formule, date d'achat) et
  // le vendeur, pas seulement l'acquéreur. Ces champs-là ne s'appuyaient que sur
  // le `defaultValue` des <Controller>, or react-hook-form ne lit un
  // `defaultValue` qu'au premier enregistrement du champ : si l'instance du
  // composant est réutilisée pour un autre dossier, la valeur du dossier
  // précédent reste en place et `getMergedData` la fait gagner sur la base. La
  // DA repartait alors au SIV avec le véhicule du dossier précédent.
  //
  // Le reset n'est plus conditionné à la présence d'un acquéreur : un dossier
  // sans buyer ne rafraîchissait aucun champ.
  useEffect(() => {
    if (!daData) return;

    const buyer = daData.buyer;
    const seller = daData.seller;
    const niceAddress = getAdresseTypeVoie(buyer?.address_name || '');
    const formattedPurchaseDate = daData.purchase_date_time ? formatUTCDateTime(daData.purchase_date_time) : '';

    setPurchaseDateTime(formattedPurchaseDate);

    formMethods.reset({
      // Véhicule : identité du dossier, elle doit toujours suivre `daData`.
      regPlate: daData.reg_plate ?? '',
      vin: daData.vin ?? '',
      numFormule: daData.num_formule ?? '',
      purchaseDateTime: formattedPurchaseDate,
      // Vendeur
      sellerClientType: seller?.client_type ?? '',
      sellerFirstName: seller?.first_name ?? '',
      sellerLastName: seller?.last_name ?? '',
      sellerCompanyName: seller?.company_name ?? '',
      sellerSiret: seller?.siret ?? '',
      // Acquéreur
      clientType: buyer?.client_type,
      firstName: buyer?.first_name,
      lastName: buyer?.last_name,
      dateOfBirth: buyer?.date_of_birth,
      addressNumber: buyer?.address_number,
      addressExtension: buyer?.address_extension,
      // Si le type de voie a fui dans address_name (legacy), on l'extrait ;
      // sinon on normalise le code stocké dans address_type (che/chm/MAJUSCULE…).
      addressType:
        niceAddress?.typeVoie && niceAddress.typeVoie !== 'sans'
          ? niceAddress.typeVoie
          : buyer?.address_type
            ? resolveVoieCode(buyer.address_type)
            : '',
      addressName: niceAddress?.nomVoie || buyer?.address_name,
      addressPlace: buyer?.address_place,
      addressStairs: buyer?.address_stairs,
      addressComp: buyer?.address_comp,
      addressPostalBox: buyer?.address_postal_box,
      city: buyer?.city,
      postalCode: buyer?.postal_code,
      country: buyer?.country,
      gender: buyer?.gender,
      documentType: buyer?.document_type,
      documentIssueDate: buyer?.document_issue_date,
      documentId: buyer?.document_id,
      siret: buyer?.siret,
      companyName: buyer?.company_name,
      placeOfBirth: buyer?.place_of_birth,
    });
  }, [daData, registrationId, formMethods]);

  useEffect(() => {
    if (daData) {
      // uploadedUrls ne contient maintenant que les nouveaux fichiers uploadés
      // Les fichiers existants sont affichés directement via FilesOpener

      // Garder les fichiers qui ont été uploadés et qui ne sont pas déjà dans les données existantes
      const existingFiles: string[] = [];

      // Collecter tous les fichiers existants pour les exclure des uploadedUrls
      if (daData.seller?.files && typeof daData.seller.files === 'object') {
        Object.values(daData.seller.files).forEach((categoryFiles) => {
          if (Array.isArray(categoryFiles)) {
            existingFiles.push(...categoryFiles);
          }
        });
      }

      if (daData.buyer?.files && typeof daData.buyer.files === 'string') {
        const buyerFiles = daData.buyer.files
          .split(',')
          .map((url: string) => url.trim())
          .filter(Boolean);
        existingFiles.push(...buyerFiles);
      }

      if (daData.buyer?.residency_files && typeof daData.buyer.residency_files === 'string') {
        const residencyFiles = daData.buyer.residency_files
          .split(',')
          .map((url: string) => url.trim())
          .filter(Boolean);
        existingFiles.push(...residencyFiles);
      }

      // Ne garder dans uploadedUrls que les nouveaux fichiers qui ne sont pas dans les données existantes
      setUploadedUrls((prevUrls) => prevUrls.filter((url) => !existingFiles.includes(url)));
    }
  }, [daData]);

  // Fonctions utilitaires pour la gestion des modifications
  const detectBuyerChanges = (formData: any, originalBuyer: any) => {
    if (!originalBuyer) return true; // Si pas de buyer original, c'est une modification

    const fieldsToCompare = [
      'firstName',
      'lastName',
      'addressNumber',
      'addressExtension',
      'addressType',
      'addressName',
      'addressPlace',
      'addressStairs',
      'addressComp',
      'addressPostalBox',
      'city',
      'postalCode',
      'country',
      'gender',
      'dateOfBirth',
      'placeOfBirth',
      'documentType',
      'documentId',
      'documentIssueDate',
      'siret',
      'companyName',
    ];

    return fieldsToCompare.some((field) => {
      const formValue = formData[field] || '';
      const originalValue = originalBuyer[field.replace(/([A-Z])/g, '_$1').toLowerCase()] || '';
      return formValue !== originalValue;
    });
  };

  const detectSellerChanges = (formData: any, originalSeller: any) => {
    if (!originalSeller) return true; // Si pas de seller original, c'est une modification

    const fieldsToCompare = ['sellerFirstName', 'sellerLastName', 'sellerCompanyName', 'sellerSiret'];

    return fieldsToCompare.some((field) => {
      const formValue = formData[field] || '';
      const dbField = field
        .replace('seller', '')
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .substring(1);
      const originalValue = originalSeller[dbField] || '';
      return formValue !== originalValue;
    });
  };

  const createNewBuyerIfChanged = async (formData: any) => {
    if (!registrationId) return false;

    const hasChanges = detectBuyerChanges(formData, daData?.buyer);

    if (hasChanges) {
      const buyerData = {
        clientType: daData?.buyer?.client_type || 'pro',
        firstName: formData.firstName,
        lastName: formData.lastName,
        addressNumber: formData.addressNumber,
        addressExtension: formData.addressExtension,
        addressType: formData.addressType,
        addressName: formData.addressName,
        addressPlace: formData.addressPlace,
        addressStairs: formData.addressStairs,
        addressComp: formData.addressComp,
        addressPostalBox: formData.addressPostalBox,
        city: formData.city,
        postalCode: formData.postalCode,
        country: formData.country,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        placeOfBirth: formData.placeOfBirth,
        documentType: formData.documentType,
        documentId: formData.documentId,
        documentIssueDate: formData.documentIssueDate,
        siret: formData.siret,
        companyName: formData.companyName,
      };

      await makeAuthenticatedApiCall('patch', `/api/da/registrations/${registrationId}/link-buyer`, buyerData);
      return true;
    }
    return false;
  };

  const createNewSellerIfChanged = async (formData: any) => {
    if (!registrationId) return false;

    const hasChanges = detectSellerChanges(formData, daData?.seller);

    if (hasChanges) {
      const sellerData = {
        clientType: formData.sellerClientType || daData?.seller?.client_type || 'person',
        firstName: formData.sellerFirstName,
        lastName: formData.sellerLastName,
        companyName: formData.sellerCompanyName,
        siret: formData.sellerSiret,
      };

      await makeAuthenticatedApiCall('patch', `/api/da/registrations/${registrationId}/link-seller`, sellerData);
      return true;
    }
    return false;
  };

  const getMergedData = async (formData?: any) => {
    if (!daData) return null;

    // Récupérer les données les plus récentes si des modifications ont été faites
    let currentDAData = daData;
    if (formData) {
      currentDAData = daData;
    }

    // Fusionner les données de registration avec les modifications du formulaire
    const mergedRegistrationData = formData
      ? {
          reg_plate: formData.regPlate || currentDAData.reg_plate,
          vin: formData.vin || currentDAData.vin,
          num_formule: formData.numFormule || currentDAData.num_formule,
          purchase_date_time: formData.purchaseDateTime || currentDAData.purchase_date_time,
        }
      : {
          reg_plate: currentDAData.reg_plate,
          vin: currentDAData.vin,
          num_formule: currentDAData.num_formule,
          purchase_date_time: currentDAData.purchase_date_time,
        };

    // Fusionner les données buyer avec les modifications du formulaire
    const mergedBuyerData = formData
      ? {
          ...currentDAData.buyer,
          first_name: formData.firstName || currentDAData.buyer?.first_name,
          last_name: formData.lastName || currentDAData.buyer?.last_name,
          address_number: formData.addressNumber || currentDAData.buyer?.address_number,
          address_extension: formData.addressExtension || currentDAData.buyer?.address_extension,
          address_type: formData.addressType || currentDAData.buyer?.address_type,
          address_name: formData.addressName || currentDAData.buyer?.address_name,
          address_place: formData.addressPlace || currentDAData.buyer?.address_place,
          address_stairs: formData.addressStairs || currentDAData.buyer?.address_stairs,
          address_comp: formData.addressComp || currentDAData.buyer?.address_comp,
          address_postal_box: formData.addressPostalBox || currentDAData.buyer?.address_postal_box,
          city: formData.city || currentDAData.buyer?.city,
          postal_code: formData.postalCode || currentDAData.buyer?.postal_code,
          country: formData.country || currentDAData.buyer?.country,
          gender: formData.gender || currentDAData.buyer?.gender,
          date_of_birth: formData.dateOfBirth || currentDAData.buyer?.date_of_birth,
          place_of_birth: formData.placeOfBirth || currentDAData.buyer?.place_of_birth,
          document_type: formData.documentType || currentDAData.buyer?.document_type,
          document_id: formData.documentId || currentDAData.buyer?.document_id,
          document_issue_date: formData.documentIssueDate || currentDAData.buyer?.document_issue_date,
          siret: formData.siret || currentDAData.buyer?.siret,
          company_name: formData.companyName || currentDAData.buyer?.company_name,
        }
      : currentDAData.buyer;

    // Fusionner les données seller avec les modifications du formulaire
    const mergedSellerData = formData
      ? {
          ...currentDAData.seller,
          client_type: formData.sellerClientType || currentDAData.seller?.client_type,
          first_name: formData.sellerFirstName || currentDAData.seller?.first_name,
          last_name: formData.sellerLastName || currentDAData.seller?.last_name,
          company_name: formData.sellerCompanyName || currentDAData.seller?.company_name,
          siret: formData.sellerSiret || currentDAData.seller?.siret,
        }
      : currentDAData.seller;

    console.log('🔄 getMergedData - Données fusionnées:', {
      originalRegistration: {
        reg_plate: currentDAData.reg_plate,
        vin: currentDAData.vin,
        num_formule: currentDAData.num_formule,
        purchase_date_time: currentDAData.purchase_date_time,
      },
      mergedRegistration: mergedRegistrationData,
      formData: formData
        ? {
            regPlate: formData.regPlate,
            vin: formData.vin,
            numFormule: formData.numFormule,
            purchaseDateTime: formData.purchaseDateTime,
          }
        : null,
    });

    return {
      ...currentDAData,
      ...mergedRegistrationData, // Fusionner les champs de registration au niveau racine
      buyer: mergedBuyerData,
      seller: mergedSellerData,
    };
  };

  const detectRegistrationChanges = (formData: any, originalData: any) => {
    if (!originalData) return false;

    const fieldsToCompare = ['regPlate', 'vin', 'numFormule', 'purchaseDateTime'];

    return fieldsToCompare.some((field) => {
      const formValue = formData[field] || '';
      let originalValue = '';

      switch (field) {
        case 'regPlate':
          originalValue = originalData.reg_plate || '';
          break;
        case 'vin':
          originalValue = originalData.vin || '';
          break;
        case 'numFormule':
          originalValue = originalData.num_formule || '';
          break;
        case 'purchaseDateTime':
          originalValue = originalData.purchase_date_time ? formatUTCDateTime(originalData.purchase_date_time) : '';
          break;
      }

      return formValue !== originalValue;
    });
  };

  const updateRegistrationIfChanged = async (formData: any) => {
    if (!registrationId) return false;

    const hasChanges = detectRegistrationChanges(formData, daData);

    if (hasChanges) {
      const registrationData = {
        regPlate: formData.regPlate,
        vin: formData.vin,
        numFormule: formData.numFormule,
        purchaseDateTime: formData.purchaseDateTime,
      };

      await makeAuthenticatedApiCall('patch', `/api/da/registrations/${registrationId}`, registrationData);

      return true;
    }
    return false;
  };

  const canRecordExternalDA = userRole === 'god' || userRole === 'arbitrator';

  const handleExternalDARecorded = () => {
    refetchDAData();
    if (onRefresh) {
      onRefresh();
    }
  };

  if (!daData) {
    return <CircularProgress />;
  }

  return (
    <>
      {userRole !== 'agency' && buttonStep === 'initial' && !isTimeoutError && (
        <div style={{ position: 'relative' }}>
          <Tooltip title={validation.tooltipError ? validation.tooltipError : ''} placement="top">
            <div style={{ position: 'relative', maxWidth: 180, margin: '0 auto' }}>
              <Button
                sx={{
                  fontSize: 14,
                  margin: '8px auto 0',
                  maxWidth: 180,
                  color: 'white',
                  backgroundColor: validation.tooltipError ? 'red' : '',
                }}
                variant="contained"
                className="dealActionButton"
                onClick={() => handleButtonClick()}
                disabled={validation.disabledButton || !!validation.tooltipError}
                fullWidth
              >
                {isLoading && <CircularProgress size={24} style={{ color: 'white', marginRight: 1 }} />}
                {validation.disabledButton ? buttonText : '📩 Soumettre la DA'}
              </Button>
              {canRecordExternalDA && (
                <ManualDAReceipt
                  registrationId={registrationId}
                  disabled={isLoading}
                  top={2}
                  onSuccess={handleExternalDARecorded}
                  openSnackbar={openSnackbar}
                />
              )}
            </div>
          </Tooltip>
          {validation.tooltipError && (
            <Typography
              color="error"
              sx={{
                fontSize: 12,
                marginTop: 1,
                textAlign: 'center',
              }}
            >
              {validation.tooltipError}
            </Typography>
          )}
        </div>
      )}

      {buttonStep === 'save' && !isTimeoutError && (
        <Tooltip
          title={
            isLoading
              ? "Demande sauvegardée, maintenant en cours d'envoi"
              : "La demande n'a pas abouti. Cliquez pour réessayer."
          }
          placement="top"
        >
          <div style={{ position: 'relative', width: 180, margin: '0 auto' }}>
            <Button
              sx={{
                fontSize: 14,
                margin: '8px auto 0',
                width: 180,
                backgroundColor: 'orange !important',
              }}
              variant="contained"
              onClick={
                responseErrorCode !== 3 && responseErrorCode !== 100
                  ? () => handleButtonClick()
                  : () => {
                      const formData = formMethods.getValues();
                      handleSubmitPurchase(formData);
                    }
              }
              fullWidth
            >
              {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
              {buttonText}
            </Button>
            {canRecordExternalDA && (
              <ManualDAReceipt
                registrationId={registrationId}
                disabled={isLoading}
                top={2}
                onSuccess={handleExternalDARecorded}
                openSnackbar={openSnackbar}
              />
            )}
            {errorText && (
              <Typography
                sx={{ fontSize: '10px', lineHeight: '1.25', marginTop: 1, fontWeight: 'bold', color: 'red' }}
                color="red"
              >
                {errorText}
              </Typography>
            )}
          </div>
        </Tooltip>
      )}

      {buttonStep === 'receipt' && !isTimeoutError && (
        <Button
          sx={{
            fontSize: 14,
            margin: '8px auto 0',
            maxWidth: 160,
            backgroundColor: 'green',
            color: 'white',
          }}
          variant="contained"
          onClick={() => handleGetReceipt(responseNomFic)}
          fullWidth
        >
          {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
          {buttonText}
        </Button>
      )}

      {buttonStep === 'regen' && !isTimeoutError && responseId !== undefined && (
        <Tooltip title="Régénérer le récépissé DA" placement="top">
          <div>
            <Button
              sx={{
                fontSize: 14,
                margin: '8px auto 0',
                width: 180,
                backgroundColor: 'orange !important',
              }}
              variant="contained"
              onClick={() => handleResendToMinistry(responseId)}
              fullWidth
            >
              {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
              {buttonText}
            </Button>
          </div>
        </Tooltip>
      )}

      {isTimeoutError && responseId !== undefined && (
        <Tooltip title="Timeout Ministère : Ré-essayez dans quelques instants" placement="top">
          <div>
            <Button
              sx={{
                fontSize: 14,
                margin: '8px auto 0',
                width: 180,
                backgroundColor: 'red !important',
              }}
              variant="contained"
              onClick={() => handleResendToMinistry(responseId)}
              fullWidth
            >
              {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
              {buttonText}
            </Button>
            {errorText && (
              <Typography
                sx={{ fontSize: '10px', lineHeight: '1.25', marginTop: 1, fontWeight: 'bold', color: 'red' }}
                color="red"
              >
                {errorText}
              </Typography>
            )}
          </div>
        </Tooltip>
      )}

      <Dialog open={isDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="lg">
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(handleSave)}>
            <DialogContent>
              {/* Bouton de téléchargement en haut à droite */}
              <Button
                onClick={handleDownloadDocuments}
                variant="contained"
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  backgroundColor: theme.palette.saffron.main,
                  '&:hover': { backgroundColor: theme.palette.saffron.main },
                  zIndex: 1000,
                  minWidth: 'auto',
                  padding: '8px 12px',
                }}
                disabled={downloading}
              >
                {downloading ? <CircularProgress size={20} style={{ color: 'white' }} /> : <SaveIcon />}
              </Button>

              <Grid container spacing={2}>
                <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
                  <Controller
                    name="purchaseDateTime"
                    control={control}
                    defaultValue={purchaseDateTime || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        autoFocus
                        margin="dense"
                        id="purchaseDateTime"
                        label="Date et heure de la vente"
                        type="datetime-local"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
                  <Controller
                    name="regPlate"
                    control={control}
                    defaultValue={daData?.reg_plate ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="regPlate"
                        label="Immatriculation"
                        type="text"
                        fullWidth
                        inputProps={{ maxLength: 9 }}
                        onChange={(e) => {
                          const upperCaseValue = e.target.value.toUpperCase();
                          field.onChange(formatRegPlate(upperCaseValue));
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
                  <Controller
                    name="vin"
                    control={control}
                    defaultValue={daData?.vin ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="vin"
                        label="VIN"
                        type="text"
                        fullWidth
                        inputProps={{ maxLength: 17 }}
                        onChange={(e) => field.onChange(removeSpaces(e.target.value))}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
                  <Controller
                    name="numFormule"
                    control={control}
                    defaultValue={daData?.num_formule ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="numFormule"
                        label="Numéro de formule"
                        type="text"
                        fullWidth
                        inputProps={{ maxLength: 11 }}
                        onChange={(e) => field.onChange(removeSpaces(e.target.value))}
                      />
                    )}
                  />
                </Grid>

                {/* Affichage des fichiers du véhicule/seller */}
                {daData?.seller?.files && typeof daData.seller.files === 'object' && (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      {(daData.seller.files as any).certificate &&
                        (daData.seller.files as any).certificate.length > 0 && (
                          <Grid item xs={12} md={2}>
                            <FilesOpener
                              onFileClick={handleCarouselOpen}
                              FilesUrls={(daData.seller.files as any).certificate.join(',')}
                            />
                          </Grid>
                        )}
                      {(daData.seller.files as any).cerfa_achat &&
                        (daData.seller.files as any).cerfa_achat.length > 0 && (
                          <Grid item xs={12} md={2}>
                            <FilesOpener
                              onFileClick={handleCarouselOpen}
                              FilesUrls={(daData.seller.files as any).cerfa_achat.join(',')}
                            />
                          </Grid>
                        )}
                      {(daData.seller.files as any).cerfa_cession &&
                        (daData.seller.files as any).cerfa_cession.length > 0 && (
                          <Grid item xs={12} md={2}>
                            <FilesOpener
                              onFileClick={handleCarouselOpen}
                              FilesUrls={(daData.seller.files as any).cerfa_cession.join(',')}
                            />
                          </Grid>
                        )}
                      {(daData.seller.files as any).mandat && (daData.seller.files as any).mandat.length > 0 && (
                        <Grid item xs={12} md={2}>
                          <FilesOpener
                            onFileClick={handleCarouselOpen}
                            FilesUrls={(daData.seller.files as any).mandat.join(',')}
                          />
                        </Grid>
                      )}
                      {(daData.seller.files as any).recepisse && (daData.seller.files as any).recepisse.length > 0 && (
                        <Grid item xs={12} md={4}>
                          <FilesOpener
                            onFileClick={handleCarouselOpen}
                            FilesUrls={(daData.seller.files as any).recepisse.join(',')}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}

                <Grid item xs={12} md={12} sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" textAlign="center">
                    Veuillez vérifier les informations de l'ancien propriétaire.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3} alignItems="center" justifyContent="center">
                  <Controller
                    name="sellerClientType"
                    control={control}
                    defaultValue={daData?.seller?.client_type ?? ''}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        <FormControlLabel
                          className="inputElement"
                          value="person"
                          control={<Radio />}
                          label="Particulier"
                        />
                        <FormControlLabel className="inputElement" value="pro" control={<Radio />} label="Pro" />
                      </RadioGroup>
                    )}
                  />
                </Grid>
                {sellerClientType === 'person' && (
                  <>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="sellerLastName"
                        control={control}
                        defaultValue={daData?.seller?.last_name ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            margin="dense"
                            id="sellerLastName"
                            label="Nom"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="sellerFirstName"
                        control={control}
                        defaultValue={daData?.seller?.first_name ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            margin="dense"
                            id="sellerFirstName"
                            label="Prénom"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                {sellerClientType === 'pro' && (
                  <>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="sellerCompanyName"
                        control={control}
                        defaultValue={daData?.seller?.company_name ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            margin="dense"
                            id="sellerCompanyName"
                            label="Nom de la société"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="sellerSiret"
                        control={control}
                        rules={{
                          validate: (value) => {
                            if (!value) return true;
                            const cleanedValue = cleanSiret(value);
                            if (cleanedValue.length > 14) return 'Le SIRET ne peut pas dépasser 14 chiffres';
                            return validateSiret(value);
                          },
                        }}
                        defaultValue={daData?.seller?.siret ?? ''}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            error={!!error}
                            helperText={error ? error.message : ''}
                            margin="dense"
                            id="sellerSiret"
                            label="Siret"
                            type="text"
                            fullWidth
                            inputProps={{ maxLength: 17 }}
                            onChange={(e) => {
                              const formattedValue = formatSiret(e.target.value);
                              const cleanedValue = cleanSiret(formattedValue);
                              if (cleanedValue.length <= 14) {
                                e.target.value = formattedValue;
                                field.onChange(cleanedValue);
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={12} sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" textAlign="center">
                    Veuillez vérifier les informations de l'acheteur.
                  </Typography>
                </Grid>

                {daData?.buyer?.client_type === 'pro' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="siret"
                        control={control}
                        rules={
                          daData?.buyer?.client_type === 'pro'
                            ? {
                                required: 'Ce champ est requis',
                                validate: (value) => {
                                  if (!value) return 'Ce champ est requis';
                                  const cleanedValue = cleanSiret(value);
                                  if (cleanedValue.length > 14) return 'Le SIRET ne peut pas dépasser 14 chiffres';
                                  return validateSiret(value);
                                },
                              }
                            : {
                                validate: (value) => {
                                  if (!value) return true;
                                  const cleanedValue = cleanSiret(value);
                                  if (cleanedValue.length > 14) return 'Le SIRET ne peut pas dépasser 14 chiffres';
                                  return validateSiret(value);
                                },
                              }
                        }
                        defaultValue={daData?.buyer?.siret ?? ''}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            error={!!error}
                            helperText={error ? error.message : ''}
                            margin="dense"
                            id="siret"
                            label="SIRET"
                            type="text"
                            fullWidth
                            inputProps={{ maxLength: 17 }}
                            onChange={(e) => {
                              const formattedValue = formatSiret(e.target.value);
                              const cleanedValue = cleanSiret(formattedValue);
                              if (cleanedValue.length <= 14) {
                                e.target.value = formattedValue;
                                field.onChange(cleanedValue);
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="companyName"
                        control={control}
                        rules={daData?.buyer?.client_type === 'pro' ? { required: 'Ce champ est requis' } : {}}
                        defaultValue={daData?.buyer?.company_name ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            margin="dense"
                            error={!!errors.companyName}
                            id="companyName"
                            className="inputElement"
                            label="Raison sociale"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} md={2}>
                  <Controller
                    name="addressNumber"
                    control={control}
                    rules={validationRules.addressNumber}
                    defaultValue={daData?.buyer?.address_number ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.addressNumber}
                        className="inputElement"
                        margin="dense"
                        id="addressNumber"
                        label="Numéro d'adresse (optionnel)"
                        placeholder="Vide si pas de numéro"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Controller
                    name="addressExtension"
                    control={control}
                    defaultValue={daData?.buyer?.address_extension ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="addressExtension"
                        label="Extension d'adresse"
                        type="text"
                        fullWidth
                        inputProps={{ maxLength: 1 }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Controller
                    name="addressType"
                    control={control}
                    rules={validationRules.addressType}
                    defaultValue={
                      resolveTypeAndNomVoie({ typeVoieLu: daData?.buyer?.address_type, nomVoie: daData?.buyer?.address_name })
                        .typeVoie || (daData?.buyer?.address_type ?? '')
                    }
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth error={!!error}>
                        <InputLabel className="inputElement" id={`${field.name}-label`}>
                          Type d'adresse
                        </InputLabel>
                        <Select
                          sx={{ minHeight: 'inherit', height: '56px', marginTop: '8px' }}
                          className="inputElement"
                          {...field}
                          labelId={`${field.name}-label`}
                          id={field.name}
                          label="Type d'adresse"
                          value={field.value ? field.value.toLowerCase() : ''}
                        >
                          <MenuItem value="rue">Rue</MenuItem>
                          <MenuItem value="all">Allée</MenuItem>
                          <MenuItem value="avn">Avenue</MenuItem>
                          <MenuItem value="blvd">Boulevard</MenuItem>
                          <MenuItem value="chau">Chaussée</MenuItem>
                          <MenuItem value="che">Chemin</MenuItem>
                          <MenuItem value="cour">Cour</MenuItem>
                          <MenuItem value="fbg">Faubourg</MenuItem>
                          <MenuItem value="fort">Fort</MenuItem>
                          <MenuItem value="imp">Impasse</MenuItem>
                          <MenuItem value="ldit">Lieu-Dit</MenuItem>
                          <MenuItem value="parc">Parc</MenuItem>
                          <MenuItem value="pass">Passage</MenuItem>
                          <MenuItem value="plc">Place</MenuItem>
                          <MenuItem value="quai">Quai</MenuItem>
                          <MenuItem value="rte">Route</MenuItem>
                          <MenuItem value="sent">Sentier</MenuItem>
                          <MenuItem value="squa">Square</MenuItem>
                          <MenuItem value="sans">Inconnu</MenuItem>
                        </Select>
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="addressName"
                    control={control}
                    rules={validationRules.addressName}
                    defaultValue={
                      resolveTypeAndNomVoie({ typeVoieLu: daData?.buyer?.address_type, nomVoie: daData?.buyer?.address_name })
                        .nomVoie
                    }
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        error={!!errors.addressName}
                        margin="dense"
                        id="addressName"
                        label="Nom de la voie (Rue, Allée, ou Avenue, ne doivent pas apparaitre ici)"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="addressPlace"
                    control={control}
                    defaultValue={daData?.buyer?.address_place ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="addressPlace"
                        label="Lieu-dit"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="addressStairs"
                    control={control}
                    defaultValue={daData?.buyer?.address_stairs ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="addressStairs"
                        label="Étage/Escalier/Appartement"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="addressComp"
                    control={control}
                    defaultValue={daData?.buyer?.address_comp ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="addressComp"
                        label="Complément d'adresse"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="addressPostalBox"
                    control={control}
                    defaultValue={daData?.buyer?.address_postal_box ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="addressPostalBox"
                        label="Boîte postale"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="city"
                    control={control}
                    rules={validationRules.city}
                    defaultValue={daData?.buyer?.city ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.city}
                        className="inputElement"
                        margin="dense"
                        id="city"
                        label="Ville"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="postalCode"
                    control={control}
                    rules={validationRules.postalCode}
                    defaultValue={daData?.buyer?.postal_code ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        error={!!errors.postalCode}
                        margin="dense"
                        id="postalCode"
                        label="Code postal"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="country"
                    control={control}
                    rules={validationRules.country}
                    defaultValue={daData?.buyer?.country ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        error={!!errors.country}
                        margin="dense"
                        id="country"
                        label="Pays"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {daData?.buyer?.client_type === 'person' && (
                  <>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="dateOfBirth"
                        rules={daData?.buyer?.client_type === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={daData?.buyer?.date_of_birth ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="dateOfBirth"
                            error={!!errors.dateOfBirth}
                            sx={{ marginTop: '8px' }}
                            className="inputElement"
                            label="Date de naissance"
                            type="date"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Controller
                        name="placeOfBirth"
                        control={control}
                        rules={daData?.buyer?.client_type === 'person' ? { required: 'Ce champ est requis' } : {}}
                        defaultValue={daData?.buyer?.place_of_birth ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            error={!!errors.placeOfBirth}
                            className="inputElement"
                            margin="dense"
                            id="placeOfBirth"
                            label="Lieu de naissance"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Controller
                        name="gender"
                        control={control}
                        rules={daData?.buyer?.client_type === 'person' ? { required: 'Ce champ est requis' } : {}}
                        defaultValue={daData?.buyer?.gender}
                        render={({ field }) => (
                          <Select
                            {...field}
                            error={!!errors.gender}
                            sx={{ minHeight: 'inherit', height: '56px', marginTop: '8px' }}
                            margin="dense"
                            fullWidth
                            className="inputElement"
                          >
                            <MenuItem value="male">Masculin</MenuItem>
                            <MenuItem value="female">Féminin</MenuItem>
                          </Select>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Controller
                        name="documentId"
                        rules={daData?.buyer?.client_type === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={daData?.buyer?.document_id ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            margin="dense"
                            error={!!errors.documentId}
                            className="inputElement"
                            id="documentId"
                            label="ID du document"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Controller
                        name="documentType"
                        rules={daData?.buyer?.client_type === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={daData?.buyer?.document_type || ''}
                        render={({ field }) => (
                          <Select
                            {...field}
                            sx={{ minHeight: 'inherit', height: '56px', marginTop: '0' }}
                            error={!!errors.documentType}
                            fullWidth
                            margin="dense"
                            className="inputElement"
                          >
                            <MenuItem value="id_card">Carte d'identité</MenuItem>
                            <MenuItem value="passport">Passeport</MenuItem>
                            <MenuItem value="driving_licence">Permis de conduire</MenuItem>
                            <MenuItem value="residence_permit">Titre de séjour</MenuItem>
                          </Select>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Controller
                        name="documentIssueDate"
                        rules={daData?.buyer?.client_type === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={daData?.buyer?.document_issue_date ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            sx={{ minHeight: 'inherit', height: '56px', marginTop: '0' }}
                            error={!!errors.documentIssueDate}
                            className="inputElement"
                            id="documentIssueDate"
                            label="Date de délivrance du document"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                {/* Affichage des fichiers de l'acheteur */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                      <Typography variant="body1" textAlign="left">
                        KBIS
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <CfnPiecesWarning />
                    </Grid>
                    {existingBuyerFiles.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">
                          Pièces déjà au dossier
                        </Typography>
                        <FirebaseUploadComponent
                          hideDropzone
                          onBeforeDelete={handleBeforeDelete}
                          onDeleteComplete={handleDeleteComplete}
                          uploadedUrls={existingBuyerFiles}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={existingBuyerFiles.length > 0 ? 6 : 4}>
                      <FirebaseUploadComponent
                        onBeforeDelete={handleBeforeDelete}
                        onDeleteComplete={handleDeleteComplete}
                        onUploadComplete={handleUploadComplete}
                        uploadedUrls={uploadedUrls.filter((url: string) => {
                          // Filtrer pour ne montrer que les nouveaux fichiers uploadés
                          const sellerFiles =
                            daData?.seller?.files && typeof daData.seller.files === 'object'
                              ? Object.values(daData.seller.files as any).flat()
                              : [];

                          const buyerFiles =
                            daData?.buyer?.files && typeof daData.buyer.files === 'string'
                              ? daData.buyer.files.split(',').map((u: string) => u.trim())
                              : [];

                          const residencyFiles =
                            daData?.buyer?.residency_files && typeof daData.buyer.residency_files === 'string'
                              ? daData.buyer.residency_files.split(',').map((u: string) => u.trim())
                              : [];

                          return (
                            !sellerFiles.includes(url) && !buyerFiles.includes(url) && !residencyFiles.includes(url)
                          );
                        })}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Section pour afficher les fichiers des commentaires externes */}
              {daData && daData.commentFilesUrls && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      📎 Fichiers des commentaires externes
                    </Typography>
                    <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={daData.commentFilesUrls} />
                  </Grid>
                </Grid>
              )}

              {/* Section pour afficher les fichiers des commentaires internes */}
              {daData && daData.internalCommentFilesUrls && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      📎 Fichiers des commentaires internes
                    </Typography>
                    <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={daData.internalCommentFilesUrls} />
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button className="secondaryDealActionButton" onClick={handleDialogClose}>
                Annuler
              </Button>
              <Button
                className="dealActionButton"
                variant="contained"
                onClick={handleSaveData}
                disabled={isSavingData}
                sx={{
                  border: '2px solid #FF6060',
                  '&:hover': {
                    border: '2px solid #FF6060',
                  },
                }}
              >
                {isSavingData && <CircularProgress size={24} style={{ color: 'white', marginRight: 8 }} />}
                Enregistrer
              </Button>
              <Button
                className="dealActionButton"
                variant="contained"
                onClick={handleConfirmClick}
                disabled={!isValid || !purchaseDateTime || isLoading}
              >
                {isLoading && <CircularProgress size={24} style={{ color: 'white', marginRight: 1 }} />}
                Confirmer
              </Button>
            </DialogActions>

            {/* Progress bar */}
            {isLoading && (
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  position: 'relative',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 6,
                  backgroundColor: theme.palette.saffron.main,
                }}
              />
            )}
          </form>
        </FormProvider>
      </Dialog>

      {/* Modale de confirmation */}
      <Dialog open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
        <DialogContent>
          <Typography>Êtes-vous sûr que toutes les informations sont correctes ?</Typography>
          {currentStep && (
            <Typography variant="body2" sx={{ mt: 2, color: 'primary.main' }}>
              {currentStep}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className="secondaryDealActionButton"
            onClick={() => setIsConfirmModalOpen(false)}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button className="dealActionButton" onClick={handleFinalConfirm} disabled={isLoading}>
            {isLoading && <CircularProgress size={20} style={{ marginRight: 8 }} />}
            Confirmer
          </Button>
        </DialogActions>

        {/* Progress bar dans la modale de confirmation */}
        {isLoading && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 4,
            }}
          />
        )}
      </Dialog>

      <ErrorMessageSnackbar
        open={validation.snackbarOpen}
        message={validation.snackbarMessage}
        handleClose={() => validation.setSnackbarOpen(false)}
        severity={validation.severity}
      />

      <FileCarouselDialog
        allFiles={allCarouselFiles}
        initialIndex={carouselIndex}
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
      />
    </>
  );
};

export default SubmitFormDA;
